import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// ── Error patterns to suppress globally ──
const SUPPRESSED_ERRORS = [
  'ResizeObserver loop',
  'Response body is already used',   // rrweb recorder clone() conflict
  "Failed to execute 'clone'",       // rrweb recorder clone() conflict
];

const isSuppressed = (msg) =>
  msg && typeof msg === 'string' && SUPPRESSED_ERRORS.some((e) => msg.includes(e));

// Suppress via window.onerror
const originalOnError = window.onerror;
window.onerror = (message, source, lineno, colno, error) => {
  if (isSuppressed(message)) return true;
  if (originalOnError) return originalOnError(message, source, lineno, colno, error);
  return false;
};

// Suppress via error event listener
window.addEventListener('error', (event) => {
  if (isSuppressed(event.message)) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return true;
  }
}, true);

// Suppress via unhandled rejection
window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message || (typeof event.reason === 'string' ? event.reason : '');
  if (isSuppressed(msg)) {
    event.stopImmediatePropagation();
    event.preventDefault();
    return true;
  }
});

// ── Patch window.fetch to make responses clone-safe ──
// The rrweb recorder intercepts fetch and calls response.clone() AFTER
// application code consumes the body via .json()/.text(). This causes
// "Response body is already used" errors. Fix: pre-clone and return
// the clone so the original stays unconsumed for rrweb.
(function patchFetchForRecorder() {
  const nativeFetch = window.fetch;
  window.fetch = function (...args) {
    return nativeFetch.apply(this, args).then((response) => {
      try {
        // Clone the response and return the clone to the caller.
        // The original Response stays unconsumed for rrweb to clone later.
        return response.clone();
      } catch {
        return response;
      }
    });
  };
})();

// Override ResizeObserver to catch errors at source
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
  constructor(callback) {
    super((entries, observer) => {
      window.requestAnimationFrame(() => {
        try {
          callback(entries, observer);
        } catch (e) {
          if (!isSuppressed(e?.message)) throw e;
        }
      });
    });
  }
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
